import { useEffect, useRef } from "react";

// Focus, Escape and background isolation are shared by cart and account dialogs.
export default function useAccessibleDialog(dialogRef, open, onClose) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const isolated = [];
    for (let branch = dialog; branch.parentElement && branch.parentElement !== document.documentElement; branch = branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || sibling.hasAttribute("data-dialog-backdrop")) continue;
        isolated.push([sibling, sibling.inert, sibling.getAttribute("aria-hidden")]);
        sibling.inert = true;
        sibling.setAttribute("aria-hidden", "true");
      }
    }
    const controls = () => [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]')].filter(el => !el.hidden && el.getClientRects().length > 0);
    const frame = requestAnimationFrame(() => (controls()[0] || dialog).focus());
    const onKeyDown = event => {
      if (event.key === "Escape") { event.preventDefault(); close.current(); return; }
      if (event.key !== "Tab") return;
      const targets = controls();
      if (!targets.length) { event.preventDefault(); dialog.focus(); return; }
      const first = targets[0], last = targets[targets.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    dialog.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      dialog.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      for (const [element, inert, hidden] of isolated) {
        element.inert = inert;
        if (hidden === null) element.removeAttribute("aria-hidden"); else element.setAttribute("aria-hidden", hidden);
      }
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [open, dialogRef]);
}
